import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { apiService } from '../services/apiService';

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const SuperAdminDataReportsPage: React.FC = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rangeParams = useMemo(() => {
    const p: { fromDate?: string; toDate?: string } = {};
    if (fromDate.trim()) p.fromDate = fromDate.trim();
    if (toDate.trim()) p.toDate = toDate.trim();
    return p;
  }, [fromDate, toDate]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AssessmentIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Data &amp; Reports
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Optional date range (applies to CSV log exports and the ZIP bundle)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave empty to include the latest records (up to export limits). Use YYYY-MM-DD.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            size="small"
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Download reports
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              run('login', () => apiService.exportAdminLoginCsv(rangeParams))
            }
          >
            {busy === 'login' ? '…' : 'Login logs (CSV)'}
          </Button>
          <Button
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              run('activity', () => apiService.exportAdminActivityCsv(rangeParams))
            }
          >
            {busy === 'activity' ? '…' : 'User activity (CSV)'}
          </Button>
          <Button
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              run('audit', () => apiService.exportAdminAuditCsv(rangeParams))
            }
          >
            {busy === 'audit' ? '…' : 'Audit logs (CSV)'}
          </Button>
          <Button
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              run('proxy', () => apiService.exportAdminProxyDownloadsCsv(rangeParams))
            }
          >
            {busy === 'proxy' ? '…' : 'Proxy downloads (CSV)'}
          </Button>
          <Button
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              run('usage', async () => {
                const response = await apiService.downloadAdminUsageSummaryJson();
                downloadBlob(response.data, 'usage-summary.json');
              })
            }
          >
            {busy === 'usage' ? '…' : 'Usage stats (JSON)'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Export system data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Single ZIP: usage summary (JSON), login history, activity logs, audit logs, and proxy download rows
          (same date range as above when set).
        </Typography>
        <Button
          variant="contained"
          disabled={!!busy}
          onClick={() =>
            run('zip', async () => {
              const response = await apiService.downloadAdminSystemBundleZip(rangeParams);
              downloadBlob(response.data, 'system-export.zip');
            })
          }
        >
          {busy === 'zip' ? 'Preparing…' : 'Download full bundle (ZIP)'}
        </Button>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Scheduled reports
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Alert severity="info">
          Optional: schedule recurring exports outside the app (for example a cron job or task runner that calls the
          same authenticated <code>/admin/reports/*</code> and <code>/admin/*/export.csv</code> endpoints). In-app
          scheduling is not configured here.
        </Alert>
      </Paper>
    </Box>
  );
};

export default SuperAdminDataReportsPage;
